import { computed } from 'vue';
import { formatDateKey, parseDateTime } from '@/js/core/utils/formatters';
import { registrationUrlForEvent } from '@/js/core/utils/calendar-events';
import {
  calendarEventDateKey,
  calendarEventEndTime,
  calendarEventSourceTimezone,
  calendarEventStartTime,
} from '@/js/core/utils/calendar-event-time';

/**
 * Builds event modal livestream display state from event payload data.
 */
export function useEventLivestreamDisplay(event, eventTimezone, clock) {
  const livestreamDetails = computed(() => event.value?.livestream_details || {});
  const muxPlaybackId = computed(() => {
    return livestreamDetails.value?.playback_ids?.find(playback => playback?.id)?.id || '';
  });
  const isEventToday = computed(() => {
    if (!calendarEventStartTime(event.value)) {
      return false;
    }

    return (
      calendarEventDateKey(event.value, eventTimezone.value) ===
      formatDateKey(new Date(clock.value), eventTimezone.value)
    );
  });
  const isEventComplete = computed(() => {
    if (!calendarEventStartTime(event.value)) {
      return false;
    }

    const completionDate =
      parseDateTime(calendarEventEndTime(event.value), calendarEventSourceTimezone(event.value)) ||
      parseDateTime(calendarEventStartTime(event.value), calendarEventSourceTimezone(event.value));

    return completionDate ? completionDate.getTime() < clock.value : false;
  });
  const isNearEventStart = computed(() => {
    if (!calendarEventStartTime(event.value)) {
      return false;
    }

    const startDate = parseDateTime(
      calendarEventStartTime(event.value),
      calendarEventSourceTimezone(event.value)
    );

    if (!startDate) {
      return false;
    }

    const oneHourBeforeStart = startDate.getTime() - 60 * 60 * 1000;
    return clock.value >= oneHourBeforeStart;
  });
  const isMuxLivestream = computed(() => livestreamDetails.value.source === 'mux');
  const isVimeoLivestream = computed(() => livestreamDetails.value.source === 'vimeo');
  const shouldRenderMuxPlayer = computed(() => {
    return (
      isMuxLivestream.value &&
      ['active', 'recording'].includes(livestreamDetails.value.live_state) &&
      Boolean(muxPlaybackId.value)
    );
  });
  const shouldRenderMuxPlaceholder = computed(() => {
    return (
      isMuxLivestream.value &&
      !shouldRenderMuxPlayer.value &&
      livestreamDetails.value.has_livestream === true &&
      isEventToday.value &&
      !isEventComplete.value &&
      isNearEventStart.value
    );
  });
  const shouldRenderVimeoPlayer = computed(() => {
    return (
      isVimeoLivestream.value &&
      Boolean(livestreamDetails.value.external_embed_url) &&
      isEventToday.value
    );
  });
  const shouldRenderLivestreamRegion = computed(() => {
    return (
      shouldRenderMuxPlayer.value ||
      shouldRenderMuxPlaceholder.value ||
      shouldRenderVimeoPlayer.value
    );
  });
  const registrationUrl = computed(() => {
    return registrationUrlForEvent(event.value);
  });
  const zoomUrl = computed(() => {
    return livestreamDetails.value.has_zoom && event.value?.zoom_link ? event.value.zoom_link : '';
  });
  const vimeoJoinUrl = computed(() => {
    return isVimeoLivestream.value && livestreamDetails.value.join_url
      ? livestreamDetails.value.join_url
      : '';
  });

  return {
    livestreamDetails,
    muxPlaybackId,
    shouldRenderMuxPlayer,
    shouldRenderVimeoPlayer,
    shouldRenderLivestreamRegion,
    registrationUrl,
    zoomUrl,
    vimeoJoinUrl,
  };
}
